import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { NavLink as RouterLink, To} from 'react-router-dom';
import { createUniqueKey, getClassNames } from '../../utils';
import { getFullWidth } from '../../utils/dom';
import HeaderNavigationDropdown, { HeaderNavigationWithSubmenu } from './HeaderNavigationDropdown';

type HeaderNavigationProps = {
    navigation?: HeaderNavigationWithSubmenu;
    labelMore?: string;
};

function HeaderNavigation({ labelMore, navigation = [] } : HeaderNavigationProps) {
    const headerNavigationRef = useRef<HTMLDivElement>(null);
    const childrenWidth = useRef<number[] | null>(null);
    const [itemsHidden, setItemsHidden] = useState(-1);
    const length = useMemo(() => navigation?.length, [navigation]);
    const calculateChildrenWidth = useCallback(() => {
        let acc = 0;

        childrenWidth.current = Object.values(headerNavigationRef.current?.childNodes || {})
            .map((item) => {
                acc += getFullWidth(item, true);

                return acc;
            });
    }, [headerNavigationRef]);

    const handleResize = useCallback(() => {
        const width = headerNavigationRef?.current?.offsetWidth || 0;
        const idxFirstInDropdown = childrenWidth?.current ? childrenWidth?.current.findIndex((item) => item > width) : -1;

        setItemsHidden(idxFirstInDropdown === -1 ? -1 : length - idxFirstInDropdown);
    }, [headerNavigationRef?.current, childrenWidth?.current, setItemsHidden]);

    const linkClassName = ({ active, hasChanges }) => getClassNames(
        'Navigation__link',
        { 'Navigation__link-active': active },
        { Navigation__link_changed: hasChanges },
    );


    const renderLink: (item: HeaderNavigationWithSubmenu) => void = ({
        title, link, active, name, hasChanges, ...rest
    }) => {

        return rest.to ? (
            <a key={title} href={rest.to.pathname} data-cy={name} target={rest.target} className={linkClassName({
                active,
                hasChanges
            })}>{title}</a>
        ):(
            <RouterLink
                {...rest}
                key={title}
                to={link as To}
                data-cy={name}
                className={linkClassName({
                    active,
                    hasChanges
                })}
            >
                {title}
            </RouterLink>
        );
    };

    const renderDropdown = ({
        title, active, hasChanges, submenu,
    }, index) => (
        <div className={`${linkClassName({ active, hasChanges })} hasDropdown`} key={createUniqueKey(index, title)}>
            <HeaderNavigationDropdown
                navigation={submenu}
                renderLink={renderLink}
                labelMore={title}
                title={title}
                key={title}
            />
        </div>
    );

    const renderMenuItem = (item, index) => {
        if (itemsHidden > 0 && item?.submenu) {
            return item?.submenu.map(renderLink);
        }

        return (
            item?.submenu
                ? renderDropdown(item, index)
                : renderLink(item)
        );
    };

    const renderNavigationDropdown = useMemo(() => {
        if (itemsHidden < 0) return null;

        const navSubIndex = navigation.findIndex((nav) => nav.submenu);
        const hasSubnav = navSubIndex !== -1;

        const initSlice = hasSubnav ? navigation.slice(0, navSubIndex) : [];
        const endSlice = hasSubnav ? navigation.slice(navSubIndex + 1, navigation.length) : [];
        const navList = hasSubnav
            ? [...initSlice, ...navigation[navSubIndex]?.submenu, ...endSlice]
            : navigation;

        return (
            <HeaderNavigationDropdown
                navigation={navList?.slice(length - itemsHidden - 1) as HeaderNavigationWithSubmenu[]}
                renderLink={renderLink}
                labelMore={labelMore as string}
                key={labelMore}
            />
        );
    }, [itemsHidden, navigation, length, labelMore]);

    useEffect(() => {
        calculateChildrenWidth();
        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className="Header__navigation" ref={headerNavigationRef}>
            {navigation.slice(0, length - itemsHidden - 1).map(renderMenuItem)}
            {renderNavigationDropdown}
        </div>
    );
}

export default HeaderNavigation;
